import { useEffect, useMemo, useRef, useState } from "react";
import {
  CircleMarker,
  MapContainer,
  Pane,
  Polyline,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { FlightReplay3D } from "./FlightReplay3D";
import { fitPoints, getMapFitPositions, type TrackPoint } from "./FlightMapView.bounds";
import { shouldShowFlightMapHeader } from "./FlightMapView.header";
import { formatProfileTime, profileDistanceAtPointer } from "./FlightMapView.profile";
import { getFlightShareUrl } from "../flight_details.helpers";

interface FlightTrackPoint extends TrackPoint {
  timestamp: string | Date;
  altitude: number;
  ground_altitude?: number;
  agl?: number;
}

interface FlightData {
  id?: number;
  title?: string;
  takeoff_coords?: string;
  json_track: FlightTrackPoint[];
  json_up_track?: FlightTrackPoint[];
}

interface FlightMapComponentProps {
  flight: FlightData;
  showHeader?: boolean;
  replay3D?: boolean;
  onReplay3DChange?: (open: boolean) => void;
}

interface ProfilePoint extends FlightTrackPoint {
  segment: string;
  distanceFromStart: number;
  terrain: number;
  altitudeAboveGround: number;
}

function getDistanceMeters(a: FlightTrackPoint, b: FlightTrackPoint): number {
  const earthRadius = 6371000;
  const lat1 = (a.coordinates.lat * Math.PI) / 180;
  const lat2 = (b.coordinates.lat * Math.PI) / 180;
  const deltaLat = ((b.coordinates.lat - a.coordinates.lat) * Math.PI) / 180;
  const deltaLon = ((b.coordinates.lon - a.coordinates.lon) * Math.PI) / 180;

  const haversine =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);

  return 2 * earthRadius * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function formatMeters(value: number): string {
  return `${Math.round(value).toLocaleString("en-US")} m`;
}

function buildProfilePoints(flight: FlightData): ProfilePoint[] {
  const segments: (FlightTrackPoint & { segment: string })[] =
    Array.isArray(flight.json_track) && flight.json_track.length
      ? flight.json_track.map((point) => ({ ...point, segment: "flight" }))
      : [];

  if (!segments.length) {
    return [];
  }

  let totalDistance = 0;

  return segments.map((point, index) => {
    const previous = segments[index - 1];

    if (previous) {
      totalDistance += getDistanceMeters(previous, point);
    }

    const terrain =
      point.segment === "up"
        ? point.altitude
        : (point.ground_altitude ?? 0) > 0
          ? point.ground_altitude!
          : Math.max(point.altitude - Math.max(point.agl || 0, 0), 0);

    return {
      ...point,
      distanceFromStart: totalDistance,
      terrain,
      altitudeAboveGround: Math.max(point.altitude - terrain, 0),
    };
  });
}

function ProfileOverlay({
  points,
  hoveredPoint,
  onHover,
  onLeave,
}: {
  points: ProfilePoint[];
  hoveredPoint: ProfilePoint | null;
  onHover: (point: ProfilePoint) => void;
  onLeave: () => void;
}) {
  const width = 1200;
  const height = 210;
  const padding = { top: 18, right: 18, bottom: 28, left: 52 };

  if (!points.length) {
    return null;
  }

  const maxDistance = points[points.length - 1].distanceFromStart || 1;
  const minAltitude = Math.min(...points.map((point) => point.terrain));
  const maxAltitude = Math.max(...points.map((point) => point.altitude));
  const altitudeSpan = Math.max(maxAltitude - minAltitude, 1);
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const toX = (distance: number) => padding.left + (distance / maxDistance) * plotWidth;
  const toY = (altitude: number) =>
    padding.top + ((maxAltitude - altitude) / altitudeSpan) * plotHeight;

  const terrainPath = [
    `M ${toX(0)} ${toY(points[0].terrain)}`,
    ...points.map((point) => `L ${toX(point.distanceFromStart)} ${toY(point.terrain)}`),
    `L ${toX(points[points.length - 1].distanceFromStart)} ${height - padding.bottom}`,
    `L ${toX(0)} ${height - padding.bottom}`,
    "Z",
  ].join(" ");

  const altitudePath = [
    `M ${toX(points[0].distanceFromStart)} ${toY(points[0].altitude)}`,
    ...points
      .slice(1)
      .map((point) => `L ${toX(point.distanceFromStart)} ${toY(point.altitude)}`),
  ].join(" ");

  const gridValues = Array.from({ length: 4 }, (_, index) => {
    const altitude = minAltitude + (altitudeSpan / 3) * index;
    return Math.round(altitude / 50) * 50;
  }).filter((value, index, values) => values.indexOf(value) === index);

  const hoveredX = hoveredPoint ? toX(hoveredPoint.distanceFromStart) : null;
  const hoveredY = hoveredPoint ? toY(hoveredPoint.altitude) : null;

  const handlePointerMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const targetDistance = profileDistanceAtPointer(
      event.clientX,
      {
        left: bounds.left,
        width: bounds.width,
        viewBoxWidth: width,
        plotLeft: padding.left,
        plotRight: padding.right,
      },
      maxDistance,
    );

    let closest = points[0];
    let smallestDelta = Math.abs(points[0].distanceFromStart - targetDistance);

    for (const point of points) {
      const delta = Math.abs(point.distanceFromStart - targetDistance);
      if (delta < smallestDelta) {
        smallestDelta = delta;
        closest = point;
      }
    }

    onHover(closest);
  };

  return (
    <div className="flview-profile-panel">
      {hoveredPoint ? (
        <div className="flview-profile-header">
          <div className="flview-profile-readout">
            <span>Alt {formatMeters(hoveredPoint.altitude)}</span>
            <span>AGL {formatMeters(hoveredPoint.altitudeAboveGround)}</span>
            <span>Time {formatProfileTime(hoveredPoint.timestamp)}</span>
          </div>
        </div>
      ) : null}

      <svg
        className="flview-profile-chart"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        onMouseMove={handlePointerMove}
        onMouseLeave={onLeave}
      >
        {gridValues.map((value) => (
          <g key={value}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={toY(value)}
              y2={toY(value)}
              className="flview-profile-grid-line"
            />
            <text x={10} y={toY(value) + 4} className="flview-profile-grid-label">
              {value} m
            </text>
          </g>
        ))}

        <path d={terrainPath} className="flview-terrain-fill" />
        <path d={altitudePath} className="flview-altitude-line" />

        {hoveredPoint ? (
          <g>
            <line
              x1={hoveredX!}
              x2={hoveredX!}
              y1={padding.top}
              y2={height - padding.bottom}
              className="flview-profile-cursor"
            />
            <circle cx={hoveredX!} cy={hoveredY!} r="5.5" className="flview-profile-point" />
          </g>
        ) : null}
      </svg>
    </div>
  );
}

function FitToBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (!positions.length || fitted.current) {
      return;
    }

    // Defer fitBounds until after the browser has painted so the map
    // container has its final CSS-driven dimensions. Without this,
    // Leaflet reads a stale/intermediate size and computes a wrong zoom.
    let raf: number;
    const fit = () => {
      raf = requestAnimationFrame(() => {
        map.invalidateSize();
        map.fitBounds(positions, {
          padding: [40, 40],
          maxZoom: 15,
          animate: false,
        });
        fitted.current = true;
      });
    };

    map.whenReady(fit);

    return () => cancelAnimationFrame(raf);
  }, [map, positions]);

  return null;
}

export function FlightMapView({ flight, showHeader, replay3D, onReplay3DChange }: FlightMapComponentProps) {
  const [showProfile, setShowProfile] = useState(true);
  const [hoveredPoint, setHoveredPoint] = useState<ProfilePoint | null>(null);
  const [showReplay3D, setShowReplay3D] = useState(false);
  const replayOpen = replay3D ?? showReplay3D;
  const setReplayOpen = onReplay3DChange ?? setShowReplay3D;
  const showMapHeader = shouldShowFlightMapHeader(showHeader);

  const upTrack = Array.isArray(flight.json_up_track) ? flight.json_up_track : [];
  const flightTrack = Array.isArray(flight.json_track) ? flight.json_track : [];
  const profilePoints = useMemo(() => buildProfilePoints(flight), [flight]);
  const mapPositions = useMemo(
    () => getMapFitPositions(flightTrack, upTrack),
    [upTrack, flightTrack],
  );
  const flightPositions = useMemo(() => fitPoints(flightTrack), [flightTrack]);
  const upPositions = useMemo(() => fitPoints(upTrack), [upTrack]);

  const startPoint = upTrack[0] || flightTrack[0] || null;
  const endPoint = flightTrack[flightTrack.length - 1] || upTrack[upTrack.length - 1] || null;
  const activePoint = hoveredPoint || endPoint;

  // Leaflet needs a valid center on mount to initialise its CRS projection.
  // Without it, CircleMarker._updateBounds crashes on latLngToLayerPoint.
  const initialCenter: [number, number] = useMemo(() => {
    const first = flightTrack[0] || upTrack[0];
    return first ? [first.coordinates.lat, first.coordinates.lon] : [46.5, 6.6];
  }, [flightTrack, upTrack]);

  // Normalize timestamps to strings for the replay component
  const replayFlightTrack = useMemo(() =>
    flightTrack.map(p => ({
      ...p,
      timestamp: typeof p.timestamp === 'string' ? p.timestamp : (p.timestamp as Date).toISOString(),
    })),
    [flightTrack],
  );

  return (
    <>
    <section className="flview-flight-map-card">
      {showMapHeader ? (
        <div className="flex items-center justify-between px-3 pt-2 pb-1">
          <span className="text-xs font-medium text-[#4F5D75] uppercase tracking-wide">Map</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowProfile((current) => !current)}
              className="flex items-center gap-1.5 text-xs text-[#4F5D75] hover:text-[#EF8354] transition-colors"
              title={showProfile ? "Hide side profile" : "Show side profile"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-4">
                <path d="M15.5 2A1.5 1.5 0 0 0 14 3.5v13a1.5 1.5 0 0 0 1.5 1.5h1a1.5 1.5 0 0 0 1.5-1.5v-13A1.5 1.5 0 0 0 16.5 2h-1ZM9.5 6A1.5 1.5 0 0 0 8 7.5v9A1.5 1.5 0 0 0 9.5 18h1a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 10.5 6h-1ZM3.5 10A1.5 1.5 0 0 0 2 11.5v5A1.5 1.5 0 0 0 3.5 18h1A1.5 1.5 0 0 0 6 16.5v-5A1.5 1.5 0 0 0 4.5 10h-1Z" />
              </svg>
              {showProfile ? "Hide profile" : "Profile"}
            </button>
            <button
              type="button"
              onClick={() => setReplayOpen(true)}
              className="flex items-center gap-1.5 text-xs text-[#4F5D75] hover:text-[#EF8354] transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-4">
                <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM3.004 10.508a7.01 7.01 0 0 0 1.473 3.865l.901-.643a.75.75 0 0 1 1.122.436l.194.97c.308.15.63.276.963.378l.497-.995a.75.75 0 0 1 1.07-.344l1.443.826A6.975 6.975 0 0 0 17 10c0-.07-.001-.14-.004-.21l-1.101.33a.75.75 0 0 1-.868-.43l-.416-.96a.75.75 0 0 0-.868-.43l-.932.28a.75.75 0 0 1-.916-.46l-.444-1.22a.75.75 0 0 0-.575-.488l-1.59-.265A6.97 6.97 0 0 0 3.004 10.508Z" clipRule="evenodd" />
              </svg>
              Replay in 3D
            </button>
          </div>
        </div>
      ) : null}

      <div className="flview-map-layout">
        <div className="flview-map-shell">
          <MapContainer className="flview-map-canvas" center={initialCenter} zoom={13} scrollWheelZoom>
            <TileLayer
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              attribution='Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="https://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
            />

            <FitToBounds positions={mapPositions} />

            <Pane name="up-track" style={{ zIndex: 410 }}>
              {upPositions.length ? (
                <Polyline
                  positions={upPositions}
                  pathOptions={{ color: "#f59e0b", weight: 4, opacity: 0.92 }}
                />
              ) : null}
            </Pane>

            <Pane name="flight-track" style={{ zIndex: 420 }}>
              {flightPositions.length ? (
                <Polyline
                  positions={flightPositions}
                  pathOptions={{ color: "#1d4ed8", weight: 4, opacity: 0.95 }}
                />
              ) : null}
            </Pane>

            {startPoint ? (
              <CircleMarker
                center={[startPoint.coordinates.lat, startPoint.coordinates.lon]}
                radius={7}
                pathOptions={{ color: "#b45309", weight: 3, fillColor: "#fef3c7", fillOpacity: 1 }}
              >
                <Tooltip direction="top" offset={[0, -8]} permanent={false}>
                  Start
                </Tooltip>
              </CircleMarker>
            ) : null}

            {endPoint ? (
              <CircleMarker
                center={[endPoint.coordinates.lat, endPoint.coordinates.lon]}
                radius={7}
                pathOptions={{ color: "#15803d", weight: 3, fillColor: "#86efac", fillOpacity: 1 }}
              >
                <Tooltip direction="top" offset={[0, -8]} permanent={false}>
                  End
                </Tooltip>
              </CircleMarker>
            ) : null}

            {activePoint ? (
              <CircleMarker
                center={[activePoint.coordinates.lat, activePoint.coordinates.lon]}
                radius={8}
                pathOptions={{ color: "#ef4444", weight: 3, fillColor: "#fff", fillOpacity: 1 }}
              />
            ) : null}
          </MapContainer>

          {showProfile ? (
            <ProfileOverlay
              points={profilePoints}
              hoveredPoint={hoveredPoint}
              onHover={setHoveredPoint}
              onLeave={() => setHoveredPoint(null)}
            />
          ) : null}
        </div>
      </div>
    </section>

    {replayOpen && replayFlightTrack.length ? (
      <FlightReplay3D
        flightTrack={replayFlightTrack}
        onClose={() => setReplayOpen(false)}
        shareUrl={flight.id ? getFlightShareUrl(flight.id, "", "3d") : undefined}
      />
    ) : null}
    </>
  );
}
